import { useState, useContext, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Link, useRouteLoaderData } from "react-router-dom";
import { ModalContext } from "../context/ModalProvider";
import { useGlobalState } from "../configurator/State";
import bmkservice from "../service/bmk-service";
import OptimizedImg from "../components/OptimizedImg";
import { incrementSession, getSession, trackModelPickerOpened, trackModelPackageSelected } from "../utils/analytics";

export default function Product({ product }) {
  const [showPackageSelector, setPackageSelector] = useState(false);
  const config = useRouteLoaderData("root");

  const [minPrice, setMinPrice] = useState(
    config.calcPrices?.includes(product.ParentGroupId) ? null : product.MinMSRP,
  );
  const [calculatedPrices, setCalculatedPrices] = useState({});
  const hp = product.HP ? product.HP : null;

  const calcPrices = useCallback(async () => {
    const cacheKey = `bmk-prices-${product.ParentGroupId}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { prices, minPrice: cachedMin } = JSON.parse(cached);
        setCalculatedPrices(prices);
        setMinPrice(cachedMin);
        return;
      }
    } catch (e) {
      /* sessionStorage unavailable — proceed with fetch */
    }

    const prices = {};
    const msrps = [];

    await Promise.all(
      product.BMKGroups.map(async (bmkGroup) => {
        await Promise.all(
          bmkGroup.ProductList.map(async (tractor) => {
            const productResponse = await fetch(
              bmkservice.getProductDetails + tractor.Name.trim(),
            );
            const productJson = await productResponse.json();

            let addOnMsrp = productJson.Tractor.DefaultTires.MsrpPrice;
            productJson.Tractor.IncludedItems.forEach((item) => {
              addOnMsrp += item.MsrpPrice;
            });

            const calculatedPrice =
              productJson.Tractor.MSRPWithoutTires + addOnMsrp;
            prices[tractor.Id] = calculatedPrice;
            msrps.push(calculatedPrice);
          }),
        );
      }),
    );

    setCalculatedPrices(prices);
    const computedMin = Math.min(...msrps);
    setMinPrice(computedMin);

    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ prices, minPrice: computedMin }),
      );
    } catch (e) {
      /* quota exceeded — prices still display, just won't cache */
    }
  }, [product.BMKGroups, product.ParentGroupId]);

  useEffect(() => {
    if (config.calcPrices?.includes(product.ParentGroupId)) {
      calcPrices();
    }
  }, [calcPrices, config.calcPrices, product.ParentGroupId]);

  const searchRegExp = /[\/]/g;
  const replaceWith = " / ";

  const openPicker = () => {
    if (showPackageSelector) return;
    const count = incrementSession('picker_opens_in_session');
    trackModelPickerOpened({ model_name: product.Name, picker_opens_in_session: count });
    setPackageSelector(true);
  };

  return (
    <div
      className="modelContainer eligible"
      onClick={openPicker}
      data-model-name={product.Name}
      data-msrp={minPrice || ""}
    >
      <span className="groupThumbContainer">
        <OptimizedImg
          src={product.ImageUrl}
          width={600}
          className="groupThumb thumb"
          alt={product.Name}
          loading="lazy"
        />
      </span>
      <span className="modelHeadline">Model</span>
      <span className="modelNameHeadline">
        {product.Name.replace(searchRegExp, replaceWith)}
      </span>
      <span className="modelDescription">{product.Description}</span>
      <span className="modelHPMsrpContainer">
        {hp && (
          <span className="hp-container centerIfOnly">
            <span className="hp-value">{hp}</span>
            <span className="hp-label">HP</span>
          </span>
        )}
        {minPrice && (
          <span className="msrp-container centerIfOnly">
            <span className="msrp-value">
              {minPrice ? "$" + minPrice.toLocaleString() : ""}
            </span>
            <span className="msrp-label">MSRP As Low As</span>
          </span>
        )}
      </span>
      {product.ParentGroupId === 124 && !minPrice ? (
        <button
          className="button orange-button choosePackage eligible"
          disabled
        >
          Loading...
        </button>
      ) : (
        <button
          className="button orange-button choosePackage eligible"
          onClick={openPicker}
        >
          Choose a Package
        </button>
      )}
      {showPackageSelector && (
        <PackageList
          product={product}
          calculatedPrices={calculatedPrices}
          minPrice={minPrice}
          closeHandler={() => setPackageSelector(false)}
        />
      )}
    </div>
  );
}

function PackageList({ product, calculatedPrices, minPrice, closeHandler }) {
  const modal = useContext(ModalContext);
  const [navMode] = useGlobalState("NavigationMode");
  // Some BMKGroups come back with no ProductList at all — skip those rather
  // than rendering an accordion section with nothing inside it.
  const nonEmptyGroups = product.BMKGroups?.filter(
    (packageGroup) => packageGroup.ProductList?.length > 0,
  );
  const defaultPackage =
    nonEmptyGroups?.[0]?.ProductList?.[0] || product.ProductList?.[0];
  const defaultGroup =
    nonEmptyGroups?.[0]?.Name ||
    (nonEmptyGroups?.[0] ? "Package Group" : undefined);
  const [selectedPackage, setPackage] = useState(defaultPackage);
  const [openGroups, setOpenGroups] = useState(
    defaultGroup ? [defaultGroup] : [],
  );

  function toggleOpenGroup(name) {
    if (openGroups.includes(name)) {
      setOpenGroups(openGroups.filter((i) => i !== name));
    } else {
      setOpenGroups([...openGroups, name]);
    }
  }

  const sendEvent = () => {
    modal.show();
    const finalPrice = selectedPackage
      ? calculatedPrices[selectedPackage.Id] || selectedPackage.MsrpPrice
      : null;
    const priceMatch = finalPrice !== null && minPrice !== null && finalPrice === minPrice;
    trackModelPackageSelected(priceMatch);
  };

  // Lock background page scroll while this panel is open. Deliberately NOT
  // the shared Modal.js technique (.scroll-locked: position: fixed on body,
  // offset by -scrollY, restored via window.scrollTo on close) — that
  // capture-and-restore round-trip depends on scrollY staying put between
  // the capture and the restore, but switching body to position: fixed
  // itself shrinks the document's scrollable height, which can force the
  // browser to immediately (and synchronously) clamp window.scrollY back to
  // 0 — a jump that's visible before our own offset math or restore ever
  // runs. That's what showed up as "jumps to the top" on open (and the
  // earlier "scroll up, then down" on close, before useLayoutEffect papered
  // over one side of it). React.StrictMode's dev-only double-invoke of this
  // effect only made it more likely to show, by giving the clamp two chances
  // to fire instead of one.
  //
  // overflow: hidden never touches scrollY at all, so there's nothing to
  // capture, clamp, or restore — the page simply can't scroll further while
  // this class is present, and removing it hands scrolling back exactly
  // where it already was. The one gap it leaves (iOS Safari's rubber-band
  // touch scroll can still bleed through overflow: hidden to the body behind
  // an overlay) is patched below: swallow touchmove on the backdrop itself,
  // but let it through inside the package list, which is meant to scroll.
  const backdropRef = useRef(null);
  useLayoutEffect(() => {
    document.body.classList.add('modelPickerOpen');

    const backdrop = backdropRef.current;
    const blockBackdropTouchScroll = (e) => {
      if (!e.target.closest('.modelPickerItemsContainer')) {
        e.preventDefault();
      }
    };
    backdrop?.addEventListener('touchmove', blockBackdropTouchScroll, { passive: false });

    return () => {
      document.body.classList.remove('modelPickerOpen');
      backdrop?.removeEventListener('touchmove', blockBackdropTouchScroll);
    };
  }, []);

  return (
    // On mobile div.modelPickerBackdrop dims the page and centers the panel
    // like the shared app modals do, without going through that shared
    // system (see bmk-react.scss); desktop neutralizes it back to a no-op
    // wrapper. e.target check so only an actual backdrop click (not one
    // bubbling up from inside the panel) closes it.
    <div
      ref={backdropRef}
      className="modelPickerBackdrop"
      onClick={(e) => { if (e.target === e.currentTarget) closeHandler(); }}
    >
      <div className="modelPicker">
        <button className="unPick" onClick={closeHandler}>
          <em className="bi bi-x"></em>
        </button>
        <div className="modelPickerHeader">Base Model Picker</div>
        <div className="modelPickerDisclaimer">
          Please select a package to get started
        </div>
        <div className="modelPickerItemsContainer">
          {nonEmptyGroups?.length > 0 ? (
            <>
              {nonEmptyGroups.map((packageGroup) => {
                let groupName = packageGroup.Name
                  ? packageGroup.Name
                  : "Package Group";
                return (
                  <div
                    key={packageGroup.Id}
                    className={
                      "modelPickerGroup" +
                      (openGroups.includes(groupName) ? " open" : "")
                    }
                  >
                    <h2 onClick={() => toggleOpenGroup(groupName)}>
                      <span>{groupName}</span>
                      <em
                        className={
                          openGroups.includes(groupName)
                            ? "bi bi-chevron-up"
                            : "bi bi-chevron-down"
                        }
                      ></em>
                    </h2>
                    {openGroups.includes(groupName) &&
                      packageGroup.ProductList?.map((productPackage) => (
                        <ProductPackage
                          key={productPackage.Id}
                          productPackage={productPackage}
                          calculatedPrices={calculatedPrices}
                          active={productPackage.Name === selectedPackage?.Name}
                          clickHandler={() => setPackage(productPackage)}
                        />
                      ))}
                  </div>
                );
              })}
              {product.ProductList?.length > 0 && (
                <div
                  className={
                    "modelPickerGroup" +
                    (openGroups.includes("other") ? " open" : "")
                  }
                >
                  <h2 onClick={() => toggleOpenGroup("other")}>
                    Other
                    <em
                      className={
                        openGroups.includes("other")
                          ? "bi bi-chevron-up"
                          : "bi bi-chevron-down"
                      }
                    ></em>
                  </h2>
                  {openGroups.includes("other") &&
                    product.ProductList.map((productPackage) => (
                      <ProductPackage
                        key={productPackage.Id}
                        productPackage={productPackage}
                        calculatedPrices={calculatedPrices}
                        active={productPackage.Name === selectedPackage?.Name}
                        clickHandler={() => setPackage(productPackage)}
                      />
                    ))}
                </div>
              )}
            </>
          ) : (
            product.ProductList.map((productPackage) => (
              <ProductPackage
                key={productPackage.Id}
                productPackage={productPackage}
                calculatedPrices={calculatedPrices}
                active={productPackage.Name === selectedPackage?.Name}
                clickHandler={() => setPackage(productPackage)}
              />
            ))
          )}
        </div>
        {selectedPackage ? (
          (() => {
            const finalPrice =
              calculatedPrices[selectedPackage.Id] || selectedPackage.MsrpPrice;
            const pickerOpens = getSession('picker_opens_in_session');
            return (
              <Link
                to={
                  encodeURIComponent(selectedPackage.Name.trim()) +
                  (navMode === "landpride" ? "/land-pride/" : "")
                }
                relative="path"
                onClick={sendEvent}
                className="startConfiguring orange button orange-button eligible"
                data-element-name="start_configuring_button"
                data-model-name={product.Name}
                data-package-name={selectedPackage.Name}
                data-package-price={finalPrice || ""}
                data-msrp-displayed={
                  minPrice ? "$" + minPrice.toLocaleString() : ""
                }
                data-picker-opens-before-select={pickerOpens}
              >
                Start Configuring
              </Link>
            );
          })()
        ) : (
          <div className="startConfiguring orange button orange-button eligible disabled">
            Start Configuring
          </div>
        )}
      </div>
    </div>
  );
}

function ProductPackage({
  productPackage,
  calculatedPrices,
  active,
  clickHandler,
}) {
  const finalPrice =
    calculatedPrices[productPackage.Id] || productPackage.MsrpPrice;
  const displayPrice = finalPrice
    ? "$" + finalPrice.toLocaleString()
    : "Call for Pricing";
  const packageDetails = productPackage.Description
    ? productPackage.Description
    : productPackage.Name;

  return (
    <div className="modelPickerItem">
      <button
        className="modelPickerRadioContainer chooseCurrentModel"
        onClick={clickHandler}
      >
        <em
          className={active ? "bi bi-check-circle-fill orange" : "bi bi-circle"}
        ></em>
      </button>
      <div className="modelPickerContentContainer">
        <span className="modelPickerContentDescription">{packageDetails}</span>
        <span className="modelPickerContentCost">{displayPrice}</span>
      </div>
    </div>
  );
}
